import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { subscriptionService } from '../services/subscriptionService'
import { contentService } from '../services/contentService'
import VideoPlayer from '../components/VideoPlayer'
import EpisodeSelector from '../components/EpisodeSelector'
import ContentMetadata from '../components/ContentMetadata'
import SubscriptionGate from '../components/SubscriptionGate'
import { Play, Plus, Check, ThumbsUp, ThumbsDown, Share2, ChevronLeft, ChevronRight, Flag } from 'lucide-react'
import { useSliceBalance } from '../hooks/useSliceBalance'
import DonateSlicesModal from '../components/DonateSlicesModal'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { useToast } from '@/hooks/use-toast'
import ReportDialog from '../components/ReportDialog'
import { useDocumentMeta } from '../hooks/useDocumentMeta'

const Video = () => {
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { toast } = useToast()

  const { balance, isActiveSubscriber } = useSliceBalance()
  const [isDonateOpen, setIsDonateOpen] = useState(false)
  const [work, setWork] = useState(null)
  const [currentEpisode, setCurrentEpisode] = useState(null)
  const [episodes, setEpisodes] = useState([])
  const [watchHistory, setWatchHistory] = useState(null)
  const [relatedContent, setRelatedContent] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isInWatchlist, setIsInWatchlist] = useState(false)
  const [userRating, setUserRating] = useState(null)
  const [ratingCounts, setRatingCounts] = useState({ likes: 0, dislikes: 0 })
  const [viewCount, setViewCount] = useState(0)
  const [error, setError] = useState(null)
  const [reportOpen, setReportOpen] = useState(false)
  const [, setSubscriptionData] = useState(null)
  const [hasSubscriptionAccess, setHasSubscriptionAccess] = useState(false)
  const [subscriptionLoading, setSubscriptionLoading] = useState(true)
  const [playbackId, setPlaybackId] = useState(null)

  useDocumentMeta({
    title: work?.title,
    description: work?.description || undefined,
    image: work?.poster_url || work?.backdrop_url || work?.thumbnail || undefined,
  })

  useEffect(() => {
    if (id) {
      fetchWork()
      fetchRatingCounts()
      if (user) {
        checkWatchlistStatus()
        fetchUserRating()
        checkSubscription()
      } else {
        setSubscriptionLoading(false)
      }
    }
  }, [id, user])

  // Set initial episode from URL param or first episode
  useEffect(() => {
    if (work && episodes.length > 0 && !currentEpisode) {
      const episodeId = searchParams.get('episode')
      if (episodeId) {
        const ep = episodes.find(e => e.id === episodeId)
        if (ep) {
          setCurrentEpisode(ep)
          return
        }
      }
      setCurrentEpisode(episodes[0])
    }
  }, [work, episodes, currentEpisode, searchParams])

  // Fetch the Mux playback ID server-side-gated via RPC once access is confirmed.
  // Playback IDs are no longer returned by public table reads.
  const activeVideoId = currentEpisode?.video_id || work?.video_id || null
  useEffect(() => {
    let cancelled = false
    setPlaybackId(null)
    if (!hasSubscriptionAccess || !activeVideoId) return
    supabase
      .rpc('get_video_playback', { p_video_id: activeVideoId })
      .then(({ data, error }) => {
        if (error) console.error('Error fetching playback ID:', error)
        if (!cancelled) setPlaybackId(error ? null : data || null)
      })
    return () => { cancelled = true }
  }, [hasSubscriptionAccess, activeVideoId])

  const fetchWork = async () => {
    try {
      setIsLoading(true)
      setError(null)

      const data = await contentService.getWorkById(id)

      if (!data) {
        setError('Content not found')
        setIsLoading(false)
        return
      }

      setWork(data)
      setViewCount(data.view_count || 0)

      // Flatten episodes from seasons for TV shows
      if (data.seasons?.length > 0) {
        const allEpisodes = data.seasons.flatMap(s =>
          (s.episodes || []).map(ep => ({
            ...ep,
            season_number: s.season_number
          }))
        )
        setEpisodes(allEpisodes)
      }

      // Fetch related content
      const related = await contentService.getRelatedContent(data.id, data.tags || [])
      setRelatedContent(related)
    } catch (err) {
      console.error('Error fetching work:', err)
      setError('Failed to load content')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchRatingCounts = async () => {
    if (!id) return
    try {
      const { data } = await supabase
        .from('user_ratings')
        .select('rating_type')
        .eq('content_id', id)
        .in('rating_type', ['like', 'dislike'])
      if (data) {
        setRatingCounts({
          likes: data.filter(r => r.rating_type === 'like').length,
          dislikes: data.filter(r => r.rating_type === 'dislike').length,
        })
      }
    } catch {
      // table may have no rows yet
    }
  }

  const handleFirstPlay = useCallback(async () => {
    if (!work) return
    const sessionKey = `view_counted_${work.id}`
    if (sessionStorage.getItem(sessionKey)) return
    sessionStorage.setItem(sessionKey, '1')
    setViewCount(prev => prev + 1)
    try {
      await supabase.rpc('increment_view_count', { p_work_id: work.id })
    } catch {
      // non-critical — optimistic update already applied
    }
  }, [work])

  const checkWatchlistStatus = async () => {
    if (!user || !id) return
    try {
      const { data } = await supabase
        .from('user_watchlist')
        .select('id')
        .eq('user_id', user.id)
        .eq('work_id', id)
        .maybeSingle()
      setIsInWatchlist(!!data)
    } catch {
      setIsInWatchlist(false)
    }
  }

  const fetchUserRating = async () => {
    if (!user || !id) return
    try {
      const { data } = await supabase
        .from('user_ratings')
        .select('rating_type')
        .eq('user_id', user.id)
        .eq('content_id', id)
        .maybeSingle()
      if (data) setUserRating(data.rating_type)
    } catch {
      // table may not exist yet
    }
  }

  const checkSubscription = async () => {
    if (!user) {
      setSubscriptionLoading(false)
      return
    }
    try {
      setSubscriptionLoading(true)
      const subscription = await subscriptionService.getUserSubscription(user.id)
      setSubscriptionData(subscription)
      // Same rule as SubscriptionGate: past_due counts as no access.
      const isPastDue = subscription?.subscriptionStatus === 'past_due'
      const meetsTier = subscriptionService.hasRequiredTier(
        subscription?.subscriptionTier || 'None',
        'Basic'
      )
      setHasSubscriptionAccess(!!subscription && !isPastDue && meetsTier)
    } catch {
      setHasSubscriptionAccess(false)
    } finally {
      setSubscriptionLoading(false)
    }
  }

  const updateWatchProgress = useCallback(async (currentTime, duration) => {
    if (!user || !work) {
      console.warn('[WatchProgress] Skipped: user=', !!user, 'work=', !!work)
      return
    }
    try {
      const targetEpisodeId = currentEpisode?.id || null
      const watchSeconds = Math.round(currentTime)
      const totalSeconds = Math.round(duration)

      // Find existing row: by episode for TV, by work_id for movies
      let query = supabase
        .from('user_watch_history')
        .select('id')
        .eq('user_id', user.id)

      if (targetEpisodeId) {
        query = query.eq('episode_new_id', targetEpisodeId)
      } else {
        query = query.eq('work_id', work.id).is('episode_new_id', null)
      }

      const { data: existing, error: selectError } = await query.maybeSingle()
      if (selectError) {
        console.error('[WatchProgress] Select failed:', selectError.message, selectError.details)
        return
      }

      const fields = {
        watch_duration_seconds: watchSeconds,
        total_duration_seconds: totalSeconds,
        last_watched_at: new Date().toISOString()
      }

      if (existing) {
        const { error: updateError } = await supabase
          .from('user_watch_history')
          .update(fields)
          .eq('id', existing.id)
        if (updateError) {
          console.error('[WatchProgress] Update failed:', updateError.message, updateError.details, updateError.hint)
        }
      } else {
        const row = {
          user_id: user.id,
          work_id: work.id,
          episode_new_id: targetEpisodeId,
          ...fields
        }
        console.log('[WatchProgress] Inserting:', row)
        const { error: insertError } = await supabase
          .from('user_watch_history')
          .insert(row)
        if (insertError) {
          console.error('[WatchProgress] Insert failed:', insertError.message, insertError.details, insertError.hint, insertError.code)
        } else {
          console.log('[WatchProgress] Insert succeeded')
        }
      }

      setWatchHistory(prev => ({
        ...prev,
        watch_duration_seconds: watchSeconds,
        total_duration_seconds: totalSeconds
      }))
    } catch (err) {
      console.error('[WatchProgress] Exception:', err)
    }
  }, [user, work, currentEpisode])

  const toggleWatchlist = useCallback(async () => {
    if (!user) {
      navigate('/auth')
      return
    }
    if (!work) return

    try {
      if (isInWatchlist) {
        await supabase
          .from('user_watchlist')
          .delete()
          .eq('user_id', user.id)
          .eq('work_id', work.id)
        setIsInWatchlist(false)
        toast({ title: 'Removed from watchlist' })
      } else {
        await supabase
          .from('user_watchlist')
          .insert({ user_id: user.id, work_id: work.id })
        setIsInWatchlist(true)
        toast({ title: 'Added to watchlist' })
      }
    } catch {
      toast({ title: 'Failed to update watchlist', variant: 'destructive' })
    }
  }, [user, work, isInWatchlist, navigate, toast])

  const handleRating = useCallback(async (ratingType) => {
    if (!user) {
      navigate('/auth')
      return
    }
    if (!work) return

    try {
      if (userRating === ratingType) {
        // Toggle off — remove the rating
        await supabase
          .from('user_ratings')
          .delete()
          .eq('user_id', user.id)
          .eq('content_id', work.id)
        setRatingCounts(prev => ({
          ...prev,
          [ratingType === 'like' ? 'likes' : 'dislikes']: Math.max(0, prev[ratingType === 'like' ? 'likes' : 'dislikes'] - 1)
        }))
        setUserRating(null)
      } else {
        await supabase
          .from('user_ratings')
          .upsert(
            { user_id: user.id, content_id: work.id, rating_type: ratingType },
            { onConflict: 'user_id,content_id' }
          )
        setRatingCounts(prev => ({
          likes: ratingType === 'like'
            ? prev.likes + 1
            : userRating === 'like' ? Math.max(0, prev.likes - 1) : prev.likes,
          dislikes: ratingType === 'dislike'
            ? prev.dislikes + 1
            : userRating === 'dislike' ? Math.max(0, prev.dislikes - 1) : prev.dislikes,
        }))
        setUserRating(ratingType)
      }
    } catch {
      toast({ title: 'Failed to update rating', variant: 'destructive' })
    }
  }, [user, work, userRating, navigate, toast])

  const handleEpisodeSelect = useCallback((episode) => {
    setCurrentEpisode(episode)
  }, [])

  const currentEpisodeIndex = currentEpisode
    ? episodes.findIndex(e => e.id === currentEpisode.id)
    : -1
  const prevEpisode = currentEpisodeIndex > 0 ? episodes[currentEpisodeIndex - 1] : null
  const nextEpisode = currentEpisodeIndex >= 0 && currentEpisodeIndex < episodes.length - 1
    ? episodes[currentEpisodeIndex + 1]
    : null

  const shareContent = useCallback(async (mode) => {
    const url = window.location.href
    const title = work?.title || 'Check out this content on Kinora'

    if (mode === 'twitter') {
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`${title} ${url}`)}`, '_blank')
      return
    }
    if (mode === 'facebook') {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank')
      return
    }

    if (navigator.share && !mode) {
      try {
        await navigator.share({ title, url })
        return
      } catch { /* cancelled */ }
    }

    try {
      await navigator.clipboard.writeText(url)
      toast({ title: 'Link copied', description: 'URL copied to clipboard.' })
    } catch {
      toast({ title: 'Copy failed', description: 'Unable to copy URL.', variant: 'destructive' })
    }
  }, [work, toast])

  const formatViews = (count) => {
    if (!count && count !== 0) return '0'
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`
    return count.toString()
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    })
  }

  const getPlaybackData = () => {
    if (currentEpisode) {
      return {
        playbackId,
        title: `${work.title} - S${currentEpisode.season_number}E${currentEpisode.episode_number}: ${currentEpisode.title}`,
        duration: currentEpisode.duration_seconds,
        startTime: 0,
        id: work?.id
      }
    }

    return {
      playbackId,
      title: work?.title,
      duration: work?.duration_seconds,
      startTime: watchHistory?.watch_duration_seconds || 0,
      id: work?.id
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background px-6 py-8 max-w-7xl mx-auto">
        <div className="space-y-6">
          <Skeleton className="w-full aspect-video rounded-lg" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="space-y-4 lg:col-span-2">
              <Skeleton className="h-8 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
              <div className="flex gap-3">
                <Skeleton className="h-9 w-24" />
                <Skeleton className="h-9 w-24" />
                <Skeleton className="h-9 w-28" />
              </div>
              <Skeleton className="h-32 w-full" />
            </div>
            <div className="space-y-3">
              <Skeleton className="h-6 w-32" />
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex gap-3">
                  <Skeleton className="w-24 h-16 rounded" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !work) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <Card className="max-w-lg w-full">
          <CardContent className="p-6 text-center space-y-3">
            <h2 className="text-2xl font-bold">Content not found</h2>
            <p className="text-muted-foreground">
              {error || "The content you're looking for doesn't exist or has been removed."}
            </p>
            <div className="flex gap-3 justify-center">
              <Button variant="secondary" onClick={() => window.history.back()}>Go Back</Button>
              <Button onClick={() => navigate('/browse')}>Browse</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  const playbackData = getPlaybackData()

  if (subscriptionLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground">Checking access permissions...</p>
        </div>
      </div>
    )
  }

  // Build content metadata object for ContentMetadata component
  const contentForMetadata = {
    ...work,
    content_type: work.type,
    genres: work.genreObjects || [],
    cast_and_crew: (work.contributors || []).map(c => ({
      id: c.id,
      name: c.name,
      role: c.character_name || c.role,
      role_type: c.role
    }))
  }

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <SubscriptionGate
        requiredTier="Basic"
        contentTitle={work?.title || 'this content'}
        className="p-6"
      >
        {/* Video Player */}
        <div className="relative">
          <VideoPlayer
            video={{
              playbackId: hasSubscriptionAccess ? playbackData.playbackId : null,
              title: playbackData.title,
              duration: playbackData.duration,
              thumbnail: work.thumbnail || work.poster_url || work.backdrop_url,
              startTime: playbackData.startTime,
              requiresSubscription: !hasSubscriptionAccess,
              id: playbackData.id
            }}
            className="w-full"
            onProgress={updateWatchProgress}
            onFirstPlay={handleFirstPlay}
          />
        </div>

        {/* Episode Prev/Next Navigation */}
        {work?.type === 'tv_show' && episodes.length > 1 && (
          <div className="flex items-center justify-between px-6 py-3 bg-card/80 border-b border-border">
            <Button
              variant="ghost"
              size="sm"
              disabled={!prevEpisode}
              onClick={() => prevEpisode && handleEpisodeSelect(prevEpisode)}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              {prevEpisode
                ? `S${prevEpisode.season_number}E${prevEpisode.episode_number}: ${prevEpisode.title}`
                : 'No previous episode'}
            </Button>
            {currentEpisode && (
              <span className="text-sm text-muted-foreground hidden sm:block">
                S{currentEpisode.season_number}E{currentEpisode.episode_number}: {currentEpisode.title}
              </span>
            )}
            <Button
              variant="ghost"
              size="sm"
              disabled={!nextEpisode}
              onClick={() => nextEpisode && handleEpisodeSelect(nextEpisode)}
            >
              {nextEpisode
                ? `S${nextEpisode.season_number}E${nextEpisode.episode_number}: ${nextEpisode.title}`
                : 'No next episode'}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}

        {/* Content Information */}
        <div className="px-6 py-8 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content Info */}
            <div className="lg:col-span-2 space-y-6">
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h1 className="text-3xl font-bold text-foreground mb-2">{work.title}</h1>
                    {work.creator?.name && (
                      <p
                        className="text-sm text-muted-foreground hover:text-primary cursor-pointer transition-colors mb-1"
                        onClick={() => navigate(`/studio/${work.creator.slug || work.creator.name}`)}
                      >
                        {work.creator.name}
                      </p>
                    )}
                    <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                      {viewCount > 0 && <span>{formatViews(viewCount)} views</span>}
                      {work.release_date && (
                        <>
                          <span>·</span>
                          <span>{formatDate(work.release_date)}</span>
                        </>
                      )}
                      {work.duration && (
                        <>
                          <span>·</span>
                          <span>{work.duration}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-3 flex-wrap">
                  <Button
                    onClick={() => handleRating('like')}
                    variant={userRating === 'like' ? 'default' : 'outline'}
                    size="sm"
                    className="rounded-full"
                  >
                    <ThumbsUp className="w-4 h-4 mr-1" />
                    {ratingCounts.likes > 0 ? formatViews(ratingCounts.likes) : 'Like'}
                  </Button>
                  <Button
                    onClick={() => handleRating('dislike')}
                    variant={userRating === 'dislike' ? 'destructive' : 'outline'}
                    size="sm"
                    className="rounded-full"
                  >
                    <ThumbsDown className="w-4 h-4 mr-1" />
                    {ratingCounts.dislikes > 0 ? formatViews(ratingCounts.dislikes) : 'Dislike'}
                  </Button>
                  <Button
                    onClick={toggleWatchlist}
                    variant={isInWatchlist ? 'default' : 'outline'}
                    size="sm"
                    className="rounded-full"
                  >
                    {isInWatchlist ? (
                      <Check className="w-4 h-4 mr-2" />
                    ) : (
                      <Plus className="w-4 h-4 mr-2" />
                    )}
                    {isInWatchlist ? 'In Watchlist' : 'Watchlist'}
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="rounded-full">
                        <Share2 className="w-4 h-4 mr-2" /> Share
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuItem onClick={() => shareContent('copy')}>Copy link</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => shareContent('twitter')}>Twitter</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => shareContent('facebook')}>Facebook</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-full text-muted-foreground hover:text-destructive"
                    onClick={() => user ? setReportOpen(true) : navigate('/auth')}
                    title="Report this content"
                  >
                    <Flag className="w-4 h-4 mr-1" /> Report
                  </Button>
                  {!user ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                      onClick={() => navigate('/auth')}
                    >
                      🍕 Sign in to slice
                    </Button>
                  ) : !isActiveSubscriber ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                      onClick={() => navigate('/pricing')}
                    >
                      🍕 Subscribe to slice
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                      disabled={balance === 0}
                      onClick={() => balance > 0 && setIsDonateOpen(true)}
                      title={balance === 0 ? 'No slices available this month' : 'Donate a slice to this title'}
                    >
                      🍕 Slice{balance !== null ? ` · ${balance}` : ''}
                    </Button>
                  )}
                </div>
              </div>

              {/* Continue Watching Progress */}
              {watchHistory && watchHistory.watch_duration_seconds > 0 && watchHistory.total_duration_seconds > 0 && (
                <div className="bg-card p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Continue Watching</span>
                    <span className="text-xs text-muted-foreground">
                      {Math.min(Math.floor((watchHistory.watch_duration_seconds / watchHistory.total_duration_seconds) * 100), 100)}% complete
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-1">
                    <div
                      className="bg-primary h-1 rounded-full transition-all duration-300"
                      style={{
                        width: `${Math.min((watchHistory.watch_duration_seconds / watchHistory.total_duration_seconds) * 100, 100)}%`
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Content Metadata */}
              <ContentMetadata content={contentForMetadata} />

              {/* Episode Selector for TV Shows */}
              {work.type === 'tv_show' && episodes.length > 0 && (
                <EpisodeSelector
                  episodes={episodes}
                  currentEpisode={currentEpisode}
                  onEpisodeSelect={handleEpisodeSelect}
                  watchHistory={watchHistory}
                />
              )}
            </div>

            {/* Sidebar - Related Content */}
            <div className="space-y-6">
              {relatedContent.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-4">More Like This</h3>
                  <div className="space-y-4">
                    {relatedContent.map((item) => (
                      <Card
                        key={item.id}
                        className="bg-card hover:bg-accent transition-colors cursor-pointer"
                        onClick={() => navigate(item.slug ? `/watch/${item.slug}` : `/video/${item.id}`)}
                      >
                        <CardContent className="p-3">
                          <div className="flex gap-3">
                            {item.thumbnail ? (
                              <img src={item.thumbnail} alt={item.title} className="w-24 h-16 object-cover rounded" />
                            ) : (
                              <div className="w-24 h-16 rounded bg-muted flex items-center justify-center">
                                <Play className="h-5 w-5 text-muted-foreground" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-sm truncate">{item.title}</h4>
                              <p className="text-xs text-muted-foreground mt-1">
                                {item.view_count > 0 ? `${formatViews(item.view_count)} views` : item.type === 'tv_show' ? 'TV Show' : 'Movie'}
                              </p>
                              {item.duration && (
                                <p className="text-xs text-muted-foreground">{item.duration}</p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </SubscriptionGate>

      {work && (
        <DonateSlicesModal
          isOpen={isDonateOpen}
          onClose={() => setIsDonateOpen(false)}
          work={work}
          balance={balance}
          isActiveSubscriber={isActiveSubscriber}
          onSuccess={() => {}}
        />
      )}

      {work && (
        <ReportDialog
          open={reportOpen}
          onClose={() => setReportOpen(false)}
          workId={work.id}
        />
      )}
    </div>
  )
}

export default Video
